import { baseRequest, getRequest } from "@/Util/utils"
import { Activity, type Exercise } from '@/Common/ActivityFunctions';
import route from "@/Util/routes";

export async function moveCalendarEvent(exercise: Exercise, newDate: Date, onStartMove: () => void, Xert = null) {
    if (!exercise.start_date) {
        Xert?.warning('<strong>Warning:</strong> Cannot move activity with no start date')
        return
    }
    const originalDate = new Date(exercise.start_date)
    if (
        originalDate === newDate
    ) {
        Xert?.warning('<strong>Warning:</strong> Trying to move an activity to the same date and time')
        return
    }
    if (Activity.isRecorded(exercise)) {
        Xert?.warning('<strong>Warning:</strong> Cannot move a recorded activity')
        return
    }
    const now = new Date()
    if (Activity.isPlanned(exercise) && newDate < now) {
        Xert?.warning('<strong>Warning:</strong> Cannot move planned activity to the past')
        return
    }
    console.log("Moving: ", exercise, "to: ", newDate)

    onStartMove()

    let postData = {};
    postData['start_date'] = newDate.toISOString();
    postData['type'] = Activity.getExerciseType(exercise)?.toLowerCase()
    postData['id'] = postData['type'] == 'activity' ? (exercise.related_activity_id || exercise.path) : exercise.workout_id;
    postData['end_date'] = new Date(newDate.getTime() + exercise.duration*1000).toISOString();
    postData['options'] = {
        edit_id: exercise.path,
        planned: newDate > now,
    }

    await window.fetch('/createCalendarEvent', {method: 'POST', body: JSON.stringify(postData), ...getRequest()});
    if (exercise.state?.includes("forecast")) {
        await Activity.clearFromCalendar(exercise); // TODO (hyde): why is this needed?
    }
}

export async function copyCalendarEvent(exercise: Exercise, newDate: Date, onStartCopy: () => void, Xert = null) {
    if (!exercise.start_date) {
        Xert?.warning('<strong>Warning:</strong> Cannot copy activity with no start date')
        return
    }
    console.log("Copying: ", exercise, "to: ", newDate)
    
    onStartCopy()

    const now = new Date()

    let postData = {};
    postData['start_date'] = newDate.toISOString();
    postData['type'] = Activity.getExerciseType(exercise)?.toLowerCase()
    postData['id'] = postData['type'] == 'activity' ? (exercise.related_activity_id || exercise.path) : exercise.workout_id;
    postData['end_date'] = new Date(newDate.getTime() + exercise.duration*1000).toISOString();
    postData['options'] = {
        planned: newDate > now,
    }

    await window.fetch('/createCalendarEvent', {method: 'POST', body: JSON.stringify(postData), ...getRequest()});
}

export async function swapCalendarEvents(exerciseA: Exercise, exerciseB: Exercise, onStartSwap: () => void, Xert = null) {
    if (!exerciseA || !exerciseB) {
        Xert?.warning('<strong>Warning:</strong> Cannot swap null activity')
        return
    }
    if (!exerciseA.start_date || !exerciseB.start_date) {
        Xert?.warning('<strong>Warning:</strong> Cannot swap activity with no start date')
        return
    }
    if (exerciseA.path === exerciseB.path) {
        Xert?.warning('<strong>Warning:</strong> Trying to swap the same activity')
        return
    }
    const dateA = new Date(exerciseA.start_date)
    const dateB = new Date(exerciseB.start_date)
    console.log("Swaping: ", exerciseA, "and ", exerciseB)
    if (!Activity.isForecastEvent(exerciseA) || !Activity.isForecastEvent(exerciseB)) {
        Xert?.warning('<strong>Warning:</strong> Can only swap forecasted activities')
        return
    }
    if (Activity.isGoalEvent(exerciseA) || Activity.isGoalEvent(exerciseB)) {
        Xert?.warning('<strong>Warning:</strong> Cannot swap goal event')
        return
    }
    if (exerciseA.forecast_index == exerciseB.forecast_index) {
        return
    }

    onStartSwap()

    const postData = {
        index1: exerciseA.forecast_index,
        index2: exerciseB.forecast_index,
        eventA: {
            id: exerciseA.path,
            edit_id: exerciseA.path,
            start_date: dateB.toISOString(),
            end_date: new Date(dateB.getTime() + exerciseA.duration * 1000).toISOString(),
            options: {
                edit_id: exerciseA.path,
                forecast_index: exerciseB.forecast_index,
                planned: true,
                original_placeholder_date: exerciseA.original_placeholder_date ?? dateA.toISOString(),
            },
        },
        eventB: {
            id: exerciseB.path,
            edit_id: exerciseB.path,
            start_date: dateA.toISOString(),
            end_date: new Date(dateA.getTime() + exerciseB.duration * 1000).toISOString(),
            options: {
                edit_id: exerciseB.path,
                forecast_index: exerciseA.forecast_index,
                planned: true,
                original_placeholder_date: exerciseB.original_placeholder_date ?? dateB.toISOString(),
            },
        },
    }

    await window.fetch(route('CalendarController@swapCalendarActivities'), {method: 'POST', body: JSON.stringify(postData), ...getRequest()});
}

export async function importCalendarWorkout(workoutFile, newDate: Date, onStartCopy: () => void, reloadCallback: (date: Date) => void, Xert = null) {
    onStartCopy()

    let postData = new FormData();
    postData.append('start_date', newDate.toISOString());
    postData.append('type', 'workout');
    postData.append('workout_file', workoutFile);
    postData.append('options', JSON.stringify({
        // planned: newDate > now,
        planned: true,
        state: ['scheduled', 'no_associate']
    }));

    await window.fetch('/createCalendarEvent', {method: 'POST', body: postData, ...baseRequest()});
    
    reloadCallback(newDate);
}

export async function importAndAssignWorkout(workoutFile, exercise, onStartCopy: () => void, reloadCallback: (date: Date) => void, Xert = null) {
    if (!exercise.start_date) {
        // TODO: Is this needed? This should never occur since an exercise without a start date wouldn't appear on the calendar in the first place.
        Xert?.warning('<strong>Warning:</strong> Cannot import workout into activity with no start date')
        return
    }
    // TODO: Make sure you can't assign workout to a XFAI goal activity.
    if (!Activity.isPlanned(exercise)) {
        Xert?.warning('<strong>Warning:</strong> You can only import a workout into a planned activity.')
        return
    }
    const originalDate = new Date(exercise.start_date)
    const newDateTime = originalDate

    onStartCopy()

    let postData = new FormData();

    // TODO: Is start_date needed when edit_id is set?
    postData.append('start_date', newDateTime.toISOString());
    postData.append('type', 'workout');

    postData.append('workout_file', workoutFile);

    let state = ['scheduled'];
    if (exercise.state?.includes('forecast')) state.push('forecast')

    postData.append('options', JSON.stringify({
        edit_id: exercise.path,
        planned: true,
        state,
    }));

    await window.fetch('/createCalendarEvent', {method: 'POST', body: postData, ...baseRequest()});
    
    reloadCallback(newDateTime);
}